import { mapToStaffPosition } from './core/noteMapping.js';
import { getNoteType } from './core/noteGeneration.js';

export const generateMusicalNotes = (
  pitch,
  intensity,
  segment,
  pitch1stDerivative,
  broadcastTime = 0
) => {
  console.group('[generateMusicalNotes] START');

  console.log('segment:', segment);
  console.log('broadcastTime:', broadcastTime);

  if (!pitch?.xs?.length || !segment?.words?.length) {
    console.warn('Missing pitch.xs or segment.words');
    console.groupEnd();
    return [];
  }

  const totalDuration = segment.end - segment.start;
  const rawPitchStart = pitch.xs[0];
  const rawPitchEnd = pitch.xs[pitch.xs.length - 1];

  console.log('raw pitch range:', rawPitchStart, '->', rawPitchEnd);
  console.log('segment range:', segment.start, '->', segment.end);
  console.log('totalDuration:', totalDuration);

  const shouldShiftPitchTimes =
    rawPitchStart < 0.5 &&
    segment.start > 0.5 &&
    rawPitchEnd <= totalDuration + 0.5;

  const timeOffset = shouldShiftPitchTimes ? segment.start : 0;

  console.log('shouldShiftPitchTimes:', shouldShiftPitchTimes);
  console.log('timeOffset applied:', timeOffset);

  const points = pitch.xs.map((time, i) => {
    const alignedTime = time + timeOffset;
    const rawPitch = Number(pitch.ys[i]);
    const pitchHz = Number.isFinite(rawPitch) && rawPitch > 0 ? rawPitch : null;

    return {
      rawTime: time,
      time: alignedTime,
      pitchHz,
      pitchST: pitchHz != null ? hzToSemitone(pitchHz) : null,
      intensity: nearestValue(intensity, alignedTime, timeOffset),
      slope: nearestValue(pitch1stDerivative, alignedTime, timeOffset)
    };
  });

  console.log('points length:', points.length);
  console.log('points sample first 5:', points.slice(0, 5));
  console.log('points sample last 5:', points.slice(-5));

  const envelope = buildContinuousEnvelope(points);
  console.log('envelope length:', envelope.length);

  let notes = [];

  for (const word of segment.words) {
    console.group(`[word] ${word.text}`);
    console.log('word range:', word.start, '->', word.end);

    const allWordPoints = points.filter(
      p => p.time >= word.start && p.time <= word.end
    );

    console.log('allWordPoints length:', allWordPoints.length);

    if (!allWordPoints.length) {
      console.warn('No points at all inside word interval');
      console.groupEnd();
      continue;
    }

    const voicedIslands = getVoicedIslands(allWordPoints);

    console.log(
      'voiced island lengths:',
      voicedIslands.map(r => r.length)
    );

    if (!voicedIslands.length) {
      console.warn('No voiced islands in word');
      console.groupEnd();
      continue;
    }

    let events = [];

    // 1) First try syllable-like nuclei from intensity peaks.
    const nuclei = findIntensityNuclei(allWordPoints, word.start, word.end);
    console.log('nuclei:', nuclei);

    if (nuclei.length >= 2) {
      const selected = nuclei
        .sort((a, b) => b.score - a.score)
        .slice(0, 2)
        .sort((a, b) => a.time - b.time);

      const split = midpoint(selected[0].time, selected[1].time);

      events = [
        {
          startTime: word.start,
          endTime: split,
          centerTime: selected[0].time,
          source: 'nucleus-1'
        },
        {
          startTime: split,
          endTime: word.end,
          centerTime: selected[1].time,
          source: 'nucleus-2'
        }
      ];

      console.log('Using 2 events from nuclei:', events);
    } else {
      // 2) Fallback: voiced islands
      const substantialIslands = voicedIslands.filter(region => {
        const regionDur = region[region.length - 1].time - region[0].time;
        return region.length >= 3 && regionDur >= 0.06;
      });

      console.log(
        'substantial island durations:',
        substantialIslands.map(r => r[r.length - 1].time - r[0].time)
      );

      if (substantialIslands.length >= 2) {
        const sortedByImportance = [...substantialIslands]
          .sort((a, b) => {
            const durA = a[a.length - 1].time - a[0].time;
            const durB = b[b.length - 1].time - b[0].time;
            return durB - durA;
          })
          .slice(0, 2)
          .sort((a, b) => a[0].time - b[0].time);

        events = sortedByImportance.map(region => ({
          startTime: region[0].time,
          endTime: region[region.length - 1].time,
          centerTime: midpoint(region[0].time, region[region.length - 1].time),
          source: 'island'
        }));

        console.log('Using 2 events from voiced islands:', events);
      } else {
        // 3) Fallback: one main region, maybe split only if very strong
        const mainRegion = getMainVoicedRegion(allWordPoints);

        if (!mainRegion.length) {
          console.warn('No main voiced region');
          console.groupEnd();
          continue;
        }

        const mainStart = mainRegion[0].time;
        const mainEnd = mainRegion[mainRegion.length - 1].time;
        const mainDuration = mainEnd - mainStart;

        const innerMain = mainRegion.filter(
          p =>
            p.time >= mainStart + mainDuration * 0.12 &&
            p.time <= mainEnd - mainDuration * 0.12
        );

        const analysisPoints = innerMain.length >= 3 ? innerMain : mainRegion;
        const stVals = analysisPoints.map(p => p.pitchST).filter(v => v != null);

        const excursion =
          stVals.length ? Math.max(...stVals) - Math.min(...stVals) : 0;

        console.log('mainRegion length:', mainRegion.length);
        console.log('mainRegion time:', mainStart, '->', mainEnd);
        console.log('analysis excursion:', excursion);

        const shouldSplitMainRegion =
          mainDuration > 0.42 &&
          excursion > 4.5;

        console.log('shouldSplitMainRegion:', shouldSplitMainRegion);

        if (!shouldSplitMainRegion) {
          events = [{
            startTime: mainStart,
            endTime: mainEnd,
            centerTime: midpoint(mainStart, mainEnd),
            source: 'main-region'
          }];
        } else {
          const splitTime = findSplitTime(analysisPoints, mainStart, mainEnd);

          events = [
            {
              startTime: mainStart,
              endTime: splitTime,
              centerTime: midpoint(mainStart, splitTime),
              source: 'main-region-split-1'
            },
            {
              startTime: splitTime,
              endTime: mainEnd,
              centerTime: midpoint(splitTime, mainEnd),
              source: 'main-region-split-2'
            }
          ];
        }

        console.log('Using events from main region:', events);
      }
    }

    // Pitch: local event pitch is primary truth. Envelope is fallback only.
    let eventPitches = events.map((evt, idx) => {
      const localPoints = points.filter(
        p => p.time >= evt.startTime && p.time <= evt.endTime && p.pitchST != null
      );

      const localST = localPoints.length
        ? median(localPoints.map(p => p.pitchST))
        : null;

      const envST = sampleEnvelopeAtTime(envelope, evt.centerTime);

      console.log(`event ${idx} localST:`, localST, 'envST:', envST);

      if (Number.isFinite(localST)) return localST;
      if (Number.isFinite(envST)) return envST;
      return 0;
    });

    console.log('eventPitches before pair smoothing:', eventPitches);

    // If 2-note word and pitches are close, force same height.
    if (eventPitches.length === 2 && Math.abs(eventPitches[0] - eventPitches[1]) < 1.0) {
      const sharedST = median(eventPitches);
      eventPitches = [sharedST, sharedST];
      console.log('Collapsed 2 pitches to same height:', sharedST);
    }

    for (let i = 0; i < events.length; i++) {
      const evt = events[i];
      const st = eventPitches[i];

      const core = getEventVoicedCore(points, evt.startTime, evt.endTime, evt.centerTime);

      const noteStart = core ? core.startTime : evt.startTime;
      const noteEnd = core ? core.endTime : evt.endTime;
      const noteCenter = core ? core.centerTime : evt.centerTime;

      console.log(`event ${i} core:`, core);
      console.log(`event ${i} final timing:`, {
        noteStart,
        noteEnd,
        noteCenter
      });

      const note = createNote(
        noteStart,
        noteEnd,
        noteCenter,
        st,
        segment,
        word,
        totalDuration,
        broadcastTime,
        i
      );

      console.log('created note:', note);
      notes.push(note);
    }

    console.groupEnd();
  }

  console.log('notes before sort:', notes);
  console.log('notes count before sort:', notes.length);

  const sortedNotes = notes.sort((a, b) => a.time - b.time);

  console.log('sortedNotes:', sortedNotes);
  console.log('sortedNotes count:', sortedNotes.length);

  console.groupEnd();
  return sortedNotes;
};

function findIntensityNuclei(points, wordStart, wordEnd) {
  const voiced = points.filter(
    p =>
      p.pitchST != null &&
      Number.isFinite(p.intensity)
  );

  if (voiced.length < 3) return [];

  const vals = voiced.map(p => Number(p.intensity) || 0);
  const smooth = movingAverage(vals, 5);
  const maxVal = Math.max(...smooth, 0);
  const minPeak = maxVal * 0.72;

  const peaks = [];

  for (let i = 1; i < voiced.length - 1; i++) {
    const prev = smooth[i - 1];
    const curr = smooth[i];
    const next = smooth[i + 1];

    if (curr >= prev && curr > next && curr >= minPeak) {
      peaks.push({
        time: voiced[i].time,
        intensity: curr,
        score: curr
      });
    }
  }

  const merged = [];
  const minSeparation = 0.12;

  for (const peak of peaks) {
    const prev = merged[merged.length - 1];
    if (!prev) {
      merged.push(peak);
      continue;
    }

    if (peak.time - prev.time < minSeparation) {
      if (peak.score > prev.score) {
        merged[merged.length - 1] = peak;
      }
    } else {
      merged.push(peak);
    }
  }

  const filtered = merged.filter(
    p =>
      p.time > wordStart + 0.04 &&
      p.time < wordEnd - 0.04
  );

  return filtered.slice(0, 3);
}

function getEventVoicedCore(points, startTime, endTime, centerTime) {
  const localPoints = points.filter(
    p =>
      p.time >= startTime &&
      p.time <= endTime &&
      p.pitchST != null
  );

  if (!localPoints.length) return null;

  const islands = getVoicedIslands(localPoints);
  if (!islands.length) return null;

  let chosen =
    islands.find(region =>
      centerTime >= region[0].time &&
      centerTime <= region[region.length - 1].time
    ) || islands.sort((a, b) => b.length - a.length)[0];

  const intensities = chosen
    .map(p => p.intensity)
    .filter(v => Number.isFinite(v));

  if (!intensities.length) {
    return {
      startTime: chosen[0].time,
      endTime: chosen[chosen.length - 1].time,
      centerTime: midpoint(chosen[0].time, chosen[chosen.length - 1].time)
    };
  }

  const maxIntensity = Math.max(...intensities);
  const threshold = maxIntensity * 0.55;

  let left = 0;
  let right = chosen.length - 1;

  while (
    left < chosen.length &&
    (!Number.isFinite(chosen[left].intensity) || chosen[left].intensity < threshold)
  ) {
    left++;
  }

  while (
    right >= 0 &&
    (!Number.isFinite(chosen[right].intensity) || chosen[right].intensity < threshold)
  ) {
    right--;
  }

  if (left > right) {
    return {
      startTime: chosen[0].time,
      endTime: chosen[chosen.length - 1].time,
      centerTime: midpoint(chosen[0].time, chosen[chosen.length - 1].time)
    };
  }

  const trimmed = chosen.slice(left, right + 1);

  return {
    startTime: trimmed[0].time,
    endTime: trimmed[trimmed.length - 1].time,
    centerTime: midpoint(trimmed[0].time, trimmed[trimmed.length - 1].time)
  };
}

function buildContinuousEnvelope(points) {
  if (!points.length) return [];

  const xs = points.map(p => p.time);
  const ys = points.map(p => p.pitchST);

  const out = ys.slice();

  let firstValid = -1;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null && Number.isFinite(out[i])) {
      firstValid = i;
      break;
    }
  }
  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];

  let lastValid = -1;
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null && Number.isFinite(out[i])) {
      lastValid = i;
      break;
    }
  }
  for (let i = lastValid + 1; i < out.length; i++) out[i] = out[lastValid];

  let i = firstValid;
  while (i < out.length) {
    if (out[i] != null && Number.isFinite(out[i])) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;
    while (end < out.length && (out[end] == null || !Number.isFinite(out[end]))) end++;
    if (end >= out.length) break;

    const x1 = xs[start];
    const x2 = xs[end];
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = xs[k];
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  let smooth = out.map(v => Number(v));

  for (let pass = 0; pass < 4; pass++) {
    const next = smooth.slice();
    for (let j = 1; j < smooth.length - 1; j++) {
      next[j] = (smooth[j - 1] + smooth[j] + smooth[j + 1]) / 3;
    }
    smooth = next;
  }

  return xs.map((x, idx) => ({
    x,
    y: smooth[idx]
  }));
}

function sampleEnvelopeAtTime(envelope, xTarget) {
  if (!envelope.length || !Number.isFinite(xTarget)) return null;
  if (envelope.length === 1) return envelope[0].y;

  if (xTarget <= envelope[0].x) return envelope[0].y;
  if (xTarget >= envelope[envelope.length - 1].x) return envelope[envelope.length - 1].y;

  for (let i = 0; i < envelope.length - 1; i++) {
    const p1 = envelope[i];
    const p2 = envelope[i + 1];

    if (xTarget >= p1.x && xTarget <= p2.x) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;
      return p1.y + (p2.y - p1.y) * t;
    }
  }

  return envelope[envelope.length - 1].y;
}

function getVoicedIslands(points) {
  if (!points.length) return [];

  const islands = [];
  let current = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i];

    if (p.pitchST != null) {
      current.push(p);
    } else if (current.length) {
      islands.push(current);
      current = [];
    }
  }

  if (current.length) islands.push(current);

  return islands;
}

function getMainVoicedRegion(points) {
  const islands = getVoicedIslands(points);
  if (!islands.length) return [];

  console.log(
    '[getMainVoicedRegion] island lengths:',
    islands.map(r => r.length)
  );

  const longest = islands.sort((a, b) => b.length - a.length)[0];
  console.log('[getMainVoicedRegion] selected length:', longest.length);

  return longest;
}

function createNote(
  startTime,
  endTime,
  anchorTime,
  pitchST,
  segment,
  word,
  totalDuration,
  broadcastTime,
  subIndex = 0
) {
  console.group('[createNote]');
  console.log('inputs:', {
    startTime,
    endTime,
    anchorTime,
    pitchST,
    wordText: word?.text,
    subIndex
  });

  const safePitchST = Number.isFinite(pitchST) ? pitchST : 0;
  const quantizedST = Math.round(safePitchST);
  const hz = semitoneToHz(quantizedST);

  console.log('quantizedST:', quantizedST);
  console.log('hz:', hz);

  const mapped = mapToStaffPosition(hz);
  console.log('mapToStaffPosition result:', mapped);

  const { staff, noteLevel, musicalNote, frequency, line } = mapped;

  const duration = Math.max(0.08, Math.min(0.9, endTime - startTime));
  const noteTypeData = getNoteType(duration, 1, 1, true);

  console.log('duration:', duration);
  console.log('getNoteType result:', noteTypeData);

  const { noteType, stemHeight, noteHeadClass } = noteTypeData;

  const note = {
    type: 'note',
    staff,
    noteLevel,
    musicalNote,
    targetFrequency: frequency,
    linePosition: line,
    noteType,
    stemHeight,
    stemDirection: line < 2 ? 'up' : 'down',
    noteHeadClass,
    position: ((anchorTime - segment.start) / totalDuration) * 100,
    time: anchorTime,
    startTime,
    endTime,
    duration,
    isActive: broadcastTime >= startTime && broadcastTime <= endTime,
    wordText: word.text,
    subIndex
  };

  console.log('created note object:', note);
  console.groupEnd();

  return note;
}

function findSplitTime(points, start, end) {
  console.group('[findSplitTime]');
  console.log('start:', start, 'end:', end);
  console.log('incoming points length:', points.length);

  const candidates = points.filter(
    p => p.time > start + 0.08 && p.time < end - 0.08
  );

  console.log('candidates length:', candidates.length);
  console.log('candidates sample:', candidates.slice(0, 10));

  if (!candidates.length) {
    const fallback = midpoint(start, end);
    console.warn('No split candidates, using midpoint:', fallback);
    console.groupEnd();
    return fallback;
  }

  let best = candidates[0];
  let bestScore = -Infinity;

  for (const p of candidates) {
    const slope = Number.isFinite(p.slope) ? p.slope : 0;
    const intensity = Number.isFinite(p.intensity) ? p.intensity : 0;
    const score = Math.abs(slope) + intensity * 0.01;

    console.log('candidate:', {
      time: p.time,
      slope,
      intensity,
      score
    });

    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }

  console.log('best split point:', best);
  console.log('bestScore:', bestScore);
  console.groupEnd();

  return best.time;
}

function nearestValue(series, alignedTime, timeOffset = 0) {
  if (!series?.xs?.length) return null;

  const queryTime = alignedTime - timeOffset;

  let best = 0;
  let bestDist = Math.abs(series.xs[0] - queryTime);

  for (let i = 1; i < series.xs.length; i++) {
    const dist = Math.abs(series.xs[i] - queryTime);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }

  return series.ys[best];
}

function movingAverage(arr, windowSize = 5) {
  const half = Math.floor(windowSize / 2);
  return arr.map((_, i) => {
    let sum = 0;
    let count = 0;

    for (let j = i - half; j <= i + half; j++) {
      if (j >= 0 && j < arr.length) {
        sum += arr[j];
        count++;
      }
    }

    return count ? sum / count : arr[i];
  });
}

function median(arr) {
  if (!arr?.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

function midpoint(a, b) {
  return (a + b) / 2;
}

function hzToSemitone(hz, ref = 55) {
  return 12 * Math.log2(hz / ref);
}

function semitoneToHz(st, ref = 55) {
  return ref * Math.pow(2, st / 12);
}